import React, { useId, useState } from 'react';
import clsx from 'clsx';
import { Check, ChevronDown, Minus, Plus } from 'lucide-react';
import { elementFieldTypeEnums as E } from '@dashboard/services/pageFieldTypes';
import { countries } from '@dashboard/services/countries/countries';
import AddressAutocompleteInput from '@dashboard/scenes/PageOverview/scenes/AddBooking/components/fields/AddressAutocompleteInput';

// The checkout's default form styles (checkout.css), so the example looks the same whatever
// the dashboard theme.
const CONTROL =
  'block w-full rounded-[4px] border border-[#F0EEEE] bg-white px-2 text-[14px] text-black shadow-[0_1px_2px_0_rgb(0_0_0/0.05)] outline-none placeholder:text-[#757575] focus:border-[#a3a3a3]';
const INPUT = clsx(CONTROL, 'h-[39px]');

function Label({ htmlFor, children, required }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block pb-1 text-[14px] font-medium leading-[21px] text-black"
    >
      {children}
      {required && <span>&#8202;*</span>}
    </label>
  );
}

function TextField({ label, required, placeholder, type = 'text', className }) {
  const id = useId();
  return (
    <div className={className}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <input id={id} type={type} placeholder={placeholder} className={INPUT} />
    </div>
  );
}

function SelectField({ label, required, placeholder, options, defaultValue = '' }) {
  const id = useId();
  return (
    <div>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <div className="relative">
        <select
          id={id}
          defaultValue={defaultValue}
          className={clsx(INPUT, 'appearance-none bg-none pr-8 invalid:text-[#757575]')}
          required
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-black" />
      </div>
    </div>
  );
}

function Choice({ type, name, label, defaultChecked }) {
  const id = useId();
  const round = type === 'radio';
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-[14px] text-black">
      <span className="relative flex size-4 shrink-0">
        <input
          id={id}
          type={type}
          name={name}
          defaultChecked={defaultChecked}
          className={clsx(
            'peer size-4 cursor-pointer appearance-none border border-[#d4d4d4] bg-white outline-none checked:border-black checked:bg-black focus-visible:ring-2 focus-visible:ring-black/20',
            round ? 'rounded-full' : 'rounded-[4px]',
          )}
        />
        {round ? (
          <span className="pointer-events-none absolute inset-0 m-auto hidden size-1.5 rounded-full bg-white peer-checked:block" />
        ) : (
          <Check className="pointer-events-none absolute inset-0 m-auto hidden size-3 text-white peer-checked:block" />
        )}
      </span>
      {label}
    </label>
  );
}

function MultipleChoice() {
  const name = useId();
  return (
    <fieldset>
      <legend className="pb-1 text-[14px] font-medium leading-[21px] text-black">
        How did you hear about us?
      </legend>
      <div className="flex flex-col gap-2 pt-1">
        {['A friend', 'Social media', 'Search'].map((label, index) => (
          <Choice key={label} type="radio" name={name} label={label} defaultChecked={index === 0} />
        ))}
      </div>
    </fieldset>
  );
}

function Phone() {
  const id = useId();
  return (
    <div>
      <Label htmlFor={id} required>
        Phone
      </Label>
      <div className="flex gap-2">
        <div className="relative w-[96px] shrink-0">
          <select aria-label="Country code" className={clsx(INPUT, 'appearance-none bg-none pr-7')}>
            <option>US +1</option>
            <option>GB +44</option>
            <option>CA +1</option>
            <option>AU +61</option>
          </select>
          <ChevronDown className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-black" />
        </div>
        <input id={id} type="tel" placeholder="Phone" className={INPUT} />
      </div>
    </div>
  );
}

const COUNTRIES = Object.fromEntries(countries.map(country => [country.alpha2, country.name]));
const STATES = ['California', 'New York', 'Texas'];

function Address() {
  const id = useId();
  const [address, setAddress] = useState({
    country: 'US',
    line1: '',
    line2: '',
    city: '',
    region: '',
    postalCode: '',
  });
  const set = (key, value) => setAddress(current => ({ ...current, [key]: value }));
  const row =
    'h-[38px] w-full rounded-none border-0 bg-white px-2 text-[14px] text-black shadow-none outline-none placeholder:text-[#757575] focus-visible:ring-0';
  const regions =
    address.region && !STATES.includes(address.region) ? [...STATES, address.region] : STATES;
  return (
    <div>
      <Label htmlFor={id} required>
        Billing address
      </Label>
      {/* No overflow clipping: the address suggestions open below the first line. */}
      <div className="rounded-[4px] border border-[#F0EEEE] shadow-[0_1px_2px_0_rgb(0_0_0/0.05)] [&>*+*]:border-t [&>*+*]:border-[#F0EEEE]">
        <div className="relative">
          <select
            aria-label="Country"
            value={address.country}
            onChange={event => set('country', event.target.value)}
            className={clsx(row, 'appearance-none rounded-t-[4px] bg-none pr-8')}
          >
            {Object.entries(COUNTRIES).map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-black" />
        </div>
        <AddressAutocompleteInput
          id={id}
          ariaLabel="Address line 1"
          placeholder="123 Main St"
          className={row}
          country={address.country}
          value={address.line1}
          onChange={value => set('line1', value)}
          onPickAddress={picked =>
            setAddress(current => ({
              ...current,
              ...picked,
              country: COUNTRIES[picked.country] ? picked.country : current.country,
            }))
          }
        />
        <input
          aria-label="Address line 2"
          placeholder="Address line 2 (optional)"
          value={address.line2}
          onChange={event => set('line2', event.target.value)}
          className={row}
        />
        <input
          aria-label="City"
          placeholder="City"
          value={address.city}
          onChange={event => set('city', event.target.value)}
          className={row}
        />
        <div className="relative">
          <select
            aria-label="State"
            value={address.region}
            onChange={event => set('region', event.target.value)}
            className={clsx(row, 'appearance-none bg-none pr-8')}
          >
            <option value="">State</option>
            {regions.map(state => (
              <option key={state}>{state}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-black" />
        </div>
        <input
          aria-label="ZIP code"
          placeholder="12345"
          value={address.postalCode}
          onChange={event => set('postalCode', event.target.value)}
          className={clsx(row, 'rounded-b-[4px]')}
        />
      </div>
    </div>
  );
}

function Quantity() {
  const id = useId();
  const [count, setCount] = useState(1);
  const step =
    'flex size-7 items-center justify-center rounded-[4px] text-black hover:bg-[#f5f5f5]';
  return (
    <div>
      <Label htmlFor={id}>Quantity</Label>
      <div className="flex h-[39px] w-[132px] items-center justify-between rounded-[4px] border border-[#F0EEEE] bg-white px-1 shadow-[0_1px_2px_0_rgb(0_0_0/0.05)]">
        <button
          type="button"
          aria-label="Fewer"
          onClick={() => setCount(current => Math.max(1, current - 1))}
          className={step}
        >
          <Minus className="size-4" />
        </button>
        <input
          id={id}
          inputMode="numeric"
          value={count}
          onChange={event => setCount(Math.max(1, Number(event.target.value) || 1))}
          className="w-10 border-0 bg-transparent p-0 text-center text-[14px] text-black shadow-none outline-none focus:ring-0"
        />
        <button
          type="button"
          aria-label="More"
          onClick={() => setCount(current => current + 1)}
          className={step}
        >
          <Plus className="size-4" />
        </button>
      </div>
    </div>
  );
}

function fieldExample(element) {
  switch (element) {
    case E.TEXT:
      return <TextField label="Team name" required placeholder="Team name" />;
    case E.TEXTAREA: {
      return <TextareaField />;
    }
    case E.SELECT:
      return (
        <SelectField
          label="T-shirt size"
          required
          placeholder="Select an option"
          options={['Small', 'Medium', 'Large']}
        />
      );
    case E.MULTIPLE_CHOICE:
      return <MultipleChoice />;
    case E.CHECKBOX:
      return <Choice type="checkbox" label="I accept the terms & conditions" />;
    case E.NUMBER:
      return (
        <TextField label="Number of guests" required placeholder="Number of guests" type="number" />
      );
    case E.EMAIL:
      return <TextField label="Second email address" placeholder="Email address" type="email" />;
    case E.PHONE:
      return <Phone />;
    case E.DATE:
      return <TextField label="Preferred date" type="date" className="w-1/2" />;
    case E.TIME:
      return <TextField label="Arrival time" type="time" className="w-1/2" />;
    case E.DATE_TIME:
      return <TextField label="Delivery slot" type="datetime-local" className="w-1/2" />;
    case E.COUNTRY:
      return (
        <SelectField
          label="Country"
          required
          options={Object.values(COUNTRIES)}
          defaultValue="United States"
        />
      );
    case E.ADDRESS:
      return <Address />;
    case E.QUANTITY:
      return <Quantity />;
    case E.TAX_ID:
      return <TextField label="Tax ID" placeholder="Tax ID" />;
    case E.PO_NUMBER:
      return <TextField label="PO Number" placeholder="PO Number" />;
    default:
      return null;
  }
}

function TextareaField() {
  const id = useId();
  return (
    <div>
      <Label htmlFor={id}>Special requests</Label>
      <textarea
        id={id}
        rows={3}
        placeholder="Anything we should know?"
        className={clsx(CONTROL, 'resize-y py-2')}
      />
    </div>
  );
}

function SkeletonField() {
  return (
    <div aria-hidden="true">
      <div className="mb-2 h-2.5 w-20 rounded-full bg-[#f4f4f4]" />
      <div className="h-[39px] rounded-[4px] border border-[#f6f5f5] bg-[#fcfcfc]" />
    </div>
  );
}

function layoutExample(item) {
  if (item.kind === 'group') {
    return (
      <div className="grid grid-cols-2 gap-4">
        <TextField label="First name" required placeholder="First name" />
        <TextField label="Last name" required placeholder="Last name" />
      </div>
    );
  }
  if (item.kind === 'discount-code') {
    return (
      <div>
        <Label htmlFor="picker-discount-code">Discount code</Label>
        <div className="relative">
          <input
            id="picker-discount-code"
            placeholder="Discount code"
            className={clsx(CONTROL, 'pr-20')}
          />
          <span className="absolute inset-y-0 right-3 flex items-center text-[14px] font-medium text-black">
            Apply
          </span>
        </div>
      </div>
    );
  }
  if (item.kind === 'divider') {
    return (
      <>
        <SkeletonField />
        <hr className="m-0 border-0 border-t border-[#F0EEEE]" />
        <SkeletonField />
      </>
    );
  }
  const subheading = item.level === 'subheading';
  return (
    <>
      <div
        className={clsx(
          'font-semibold text-black',
          subheading ? 'text-[16px] leading-6' : 'text-[18px] leading-[26px]',
        )}
      >
        {subheading ? 'Delivery' : 'Your details'}
      </div>
      <SkeletonField />
    </>
  );
}

export default function FieldExample({ item }) {
  return (
    <div
      data-testid="picker-example"
      className="flex w-full max-w-[448px] flex-col gap-4 font-[Inter,sans-serif]"
    >
      {item.preset ? (
        <TextField label={item.preset.label} required placeholder={item.preset.placeholder} />
      ) : item.kind === 'field' ? (
        fieldExample(item.element)
      ) : (
        layoutExample(item)
      )}
    </div>
  );
}
